import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const navLinks = [
  { href: '/kangaekata', label: '考え方' },
  { href: '/dekirukoto', label: 'できること' },
  { href: '/cases', label: '事例' },
  { href: '/company', label: '会社情報' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 40);
      const docH = document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress(docH > 0 ? (y / docH) * 100 : 0);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'bg-warm-white/96 backdrop-blur-md border-b border-divider' : 'bg-transparent'}`}
      style={{
        opacity: mounted ? 1 : 0,
        transform: mounted ? 'translateY(0)' : 'translateY(-12px)',
        transition: 'opacity 0.6s ease 0.1s, transform 0.6s ease 0.1s, background-color 0.5s ease, border-color 0.5s ease',
      }}
    >
      {/* Scroll progress bar */}
      <div
        className="absolute bottom-0 left-0 h-px bg-accent-teal transition-all duration-100 ease-out"
        style={{ width: `${scrollProgress}%`, opacity: scrolled ? 1 : 0 }}
      />

      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-3 flex-shrink-0 group"
            style={{ transition: 'opacity 0.2s ease' }}
          >
            <img
              src="https://static.readdy.ai/image/2971b814dc462dd4b8fae3beb50e2175/fe51ca6d464359b6727fc714a105c082.png"
              alt="mecena Intercom"
              className="h-8 w-auto object-contain transition-opacity duration-200 group-hover:opacity-70"
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1 relative">
            <span
              className="text-[9px] font-sans-jp text-muted-light tracking-widest-jp mr-5 select-none"
              style={{ writingMode: 'vertical-rl' }}
            >
              NAVIGATION
            </span>
            {navLinks.map((link, i) => {
              const isActive = location.pathname === link.href;
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`relative px-4 py-2 text-sm font-sans-jp tracking-wide-jp transition-colors duration-200 group whitespace-nowrap ${isActive ? 'text-accent-teal' : 'text-charcoal hover:text-accent-teal'}`}
                  style={{
                    opacity: mounted ? 1 : 0,
                    transform: mounted ? 'translateY(0)' : 'translateY(-8px)',
                    transition: `opacity 0.5s ease ${0.15 + i * 0.07}s, transform 0.5s ease ${0.15 + i * 0.07}s, color 0.2s ease`,
                  }}
                >
                  {link.label}
                  <span
                    className="absolute bottom-0 left-4 right-4 h-px bg-accent-teal transition-transform duration-300 origin-left"
                    style={{ transform: isActive ? 'scaleX(1)' : 'scaleX(0)' }}
                  />
                  {!isActive && (
                    <span className="absolute bottom-0 left-4 right-4 h-px bg-accent-teal transition-transform duration-300 origin-left scale-x-0 group-hover:scale-x-100" />
                  )}
                </Link>
              );
            })}
            <Link
              to="/contact"
              className="ml-6 px-5 py-2 text-sm font-sans-jp tracking-wide-jp border border-charcoal text-charcoal hover:bg-charcoal hover:text-warm-white transition-all duration-300 whitespace-nowrap"
              style={{
                opacity: mounted ? 1 : 0,
                transform: mounted ? 'translateY(0)' : 'translateY(-8px)',
                transition: `opacity 0.5s ease 0.43s, transform 0.5s ease 0.43s, background-color 0.3s ease, color 0.3s ease`,
              }}
            >
              相談する
            </Link>
          </nav>

          {/* Mobile hamburger */}
          <button
            className="md:hidden flex flex-col gap-1.5 p-2 cursor-pointer"
            aria-label="メニュー"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <span className={`block w-6 h-px bg-charcoal transition-all duration-300 ${menuOpen ? 'rotate-45 translate-y-2' : ''}`} />
            <span className={`block w-6 h-px bg-charcoal transition-all duration-300 ${menuOpen ? 'opacity-0' : ''}`} />
            <span className={`block w-6 h-px bg-charcoal transition-all duration-300 ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`} />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div className={`md:hidden bg-warm-white border-t border-divider transition-all duration-400 overflow-hidden ${menuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
        <nav className="px-6 py-6 flex flex-col gap-4">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className={`text-base font-sans-jp tracking-wide-jp py-2 border-b border-divider transition-colors duration-200 ${location.pathname === link.href ? 'text-accent-teal' : 'text-charcoal'}`}
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/contact"
            className="mt-2 px-5 py-3 text-sm font-sans-jp tracking-wide-jp border border-charcoal text-charcoal text-center whitespace-nowrap"
          >
            相談する
          </Link>
        </nav>
      </div>
    </header>
  );
}
